/**
 * The Client switcher (Story 7): every connected Client the caller may
 * see, plus "All Clients", which is the cross-Client Overview.
 *
 * Since task 201 it is the Client strip as well: one control, in the
 * header's second row on every page, that both says which Client the
 * screen is showing and changes it. Before, a native select on the right
 * of the header and a read-only strip under it said the same thing twice.
 *
 * It is a pill with a listbox behind it rather than a `<select>`: the
 * list has a find box, because an MSP with forty Clients should not
 * scroll an option list, and it can show the platform-operations tenant
 * (task 200) under its own heading, which an `<optgroup>` did but could
 * not style.
 *
 * The pill's color is the state (task 60): white with the Client's name
 * when one is selected; translucent "All Clients" on a page that is about
 * every Client; amber "No Client selected" otherwise, so a screen that
 * would otherwise ask for a selection is never a surprise.
 *
 * Since task 208 (Story 48) the list is the caller's part of the
 * organization tree: each MSP is selectable as "All of ‹MSP›", which the
 * All-Clients screens pass to the API as `?org=`, and its Clients sit
 * beneath it. The find box searches the whole tree and says where each
 * match sits. Before the tree exists the list is flat, as it was.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import type { ClientTenant, OrgNode } from '@bluesphere/api-client';

import { useSelectedTenant } from '../auth/useSelectedTenant';
import { depthOf, isParentKind, orgLabel, pathTo } from '../lib/orgs';
import {
  CurrentDot,
  MenuDivider,
  MenuPanel,
  menuItemClass,
  menuItemCurrentClass,
} from './nav/MenuPanel';
import { Chevron } from './nav/SectionNav';
import { isInService } from '../lib/clientStatus';

export function ClientSwitcher({
  allClientsPage = false,
  hint = null,
}: {
  /** The page is about every Client (Organizations, Integrations, Users, Preferences). */
  allClientsPage?: boolean;
  /** Said beside the pill, e.g. "showing all Clients" or "this page covers every Client". */
  hint?: string | null;
}) {
  const {
    tenantId,
    selected,
    orgId,
    selectedOrg,
    tenants,
    orgs,
    isLoading,
    setTenantId,
    setOrgId,
  } = useSelectedTenant();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);

  const consented = useMemo(() => tenants.filter(isInService), [tenants]);
  const consentedIds = useMemo(
    () => new Set(consented.map((t) => t.client_tenant_id)),
    [consented],
  );
  const q = query.trim().toLowerCase();
  const matches = (name: string | null | undefined) => !q || (name ?? '').toLowerCase().includes(q);
  const treeMode = orgs.homeId !== null;

  // Flat mode (no tree yet): as before task 208.
  const clients = consented.filter((t) => t.kind !== 'operations' && matches(t.display_name));
  const operations = consented.filter((t) => t.kind === 'operations' && matches(t.display_name));

  // Tree mode: the visible tree in order, Clients only once connected; the
  // operations tenant listed apart, as before.
  const treeRows = useMemo(
    () =>
      orgs.ordered.filter(
        (n) => isParentKind(n) || (n.kind === 'client' && consentedIds.has(n.org_id)),
      ),
    [orgs.ordered, consentedIds],
  );
  const treeOps = orgs.ordered.filter(
    (n) => n.kind === 'operations' && consentedIds.has(n.org_id) && matches(n.name),
  );
  const homeDepth = orgs.homeId ? depthOf(orgs, orgs.homeId) : 0;
  const shown = treeMode ? treeRows.filter((n) => matches(n.name)) : [];
  const total = treeMode ? treeRows.length + treeOps.length : consented.length;
  const showFind = total > 6;

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  const chooseClient = (id: string | null) => {
    setTenantId(id);
    close();
    buttonRef.current?.focus();
  };
  const chooseOrg = (id: string | null) => {
    setOrgId(id);
    close();
    buttonRef.current?.focus();
  };

  const label = tenantId
    ? (selected?.display_name ?? 'Loading…')
    : orgId
      ? `All of ${orgLabel(selectedOrg)}`
      : allClientsPage
        ? 'All Clients'
        : 'No Client selected';
  // On a page about every Client the selection still shows (it is what the
  // next Client-scoped page will use) but reads as not driving this one.
  const tone = allClientsPage ? 'all' : tenantId || orgId ? 'client' : 'none';

  const orgOption = (n: OrgNode) => {
    const isHome = n.org_id === orgs.homeId;
    const where = q ? pathTo(orgs, n.org_id).slice(0, -1) : [];
    return (
      <Option
        key={n.org_id}
        label={isHome ? 'All Clients' : `All of ${orgLabel(n)}`}
        detail={q && where.length > 0 ? where.map((p) => orgLabel(p)).join(' › ') : undefined}
        indent={q ? 0 : depthOf(orgs, n.org_id) - homeDepth}
        current={isHome ? !tenantId && !orgId : orgId === n.org_id}
        muted={n.status !== 'active'}
        onSelect={() => chooseOrg(isHome ? null : n.org_id)}
      />
    );
  };
  const clientOption = (n: OrgNode) => {
    const where = q ? pathTo(orgs, n.org_id).slice(0, -1) : [];
    return (
      <Option
        key={n.org_id}
        label={orgLabel(n)}
        detail={q && where.length > 0 ? where.map((p) => orgLabel(p)).join(' › ') : undefined}
        indent={q ? 0 : depthOf(orgs, n.org_id) - homeDepth}
        current={n.org_id === tenantId}
        onSelect={() => chooseClient(n.org_id)}
      />
    );
  };

  return (
    <div className="relative flex min-w-0 items-center gap-2" aria-live="polite">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Selected organization or Client"
        disabled={isLoading && consented.length === 0}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'inline-flex h-8 max-w-full min-w-0 items-center gap-2 rounded-full pl-3.5 pr-2.5 text-sm font-semibold',
          'transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-bluesphere-secondary',
          'focus-visible:ring-offset-2 focus-visible:ring-offset-bluesphere-bg disabled:cursor-wait',
          tone === 'client' && 'bg-white text-bluesphere-primary hover:bg-white/90',
          tone === 'all' && 'bg-white/10 text-white hover:bg-white/20',
          tone === 'none' && 'text-[#FFD9B5] hover:brightness-110',
        )}
        style={tone === 'none' ? { backgroundColor: 'rgba(245,131,26,0.18)' } : undefined}
      >
        <span className="truncate">{label}</span>
        <Chevron />
      </button>
      {hint && <span className="hidden truncate text-xs text-white/50 sm:inline">· {hint}</span>}

      <MenuPanel
        open={open}
        onClose={close}
        triggerRef={buttonRef}
        role="listbox"
        label={treeMode ? 'Organizations and Clients' : 'Clients'}
        className="w-80 max-w-[calc(100vw-2rem)]"
      >
        {showFind && (
          <div className="p-1 pb-2">
            <input
              id="client-switcher-find"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={treeMode ? 'Find an organization or Client' : 'Find a Client'}
              aria-label={treeMode ? 'Find an organization or Client' : 'Find a Client'}
              autoComplete="off"
              className="w-full rounded-input border border-bluesphere-border bg-white px-2.5 py-2 text-sm text-bluesphere-textOnLight placeholder:text-bluesphere-muted focus:border-transparent focus:outline-hidden focus:ring-2 focus:ring-bluesphere-secondary"
            />
          </div>
        )}

        <div className="bs-scroll max-h-[min(60vh,22rem)] overflow-y-auto">
          {treeMode ? (
            <>
              {shown.map((n) => (isParentKind(n) ? orgOption(n) : clientOption(n)))}
              {treeOps.length > 0 && (
                <>
                  {shown.length > 0 && <MenuDivider />}
                  <OpsHeading />
                  {treeOps.map(clientOption)}
                </>
              )}
              {q && shown.length === 0 && treeOps.length === 0 && (
                <div className="px-3 py-3 text-sm text-bluesphere-muted">
                  Nothing matches “{query}”.
                </div>
              )}
            </>
          ) : (
            <>
              {!q && (
                <>
                  <Option
                    label="All Clients"
                    current={!tenantId}
                    onSelect={() => chooseClient(null)}
                  />
                  {(clients.length > 0 || operations.length > 0) && <MenuDivider />}
                </>
              )}
              {clients.map((t: ClientTenant) => (
                <Option
                  key={t.client_tenant_id}
                  label={t.display_name}
                  current={t.client_tenant_id === tenantId}
                  onSelect={() => chooseClient(t.client_tenant_id)}
                />
              ))}
              {operations.length > 0 && (
                <>
                  {clients.length > 0 && <MenuDivider />}
                  <OpsHeading />
                  {operations.map((t) => (
                    <Option
                      key={t.client_tenant_id}
                      label={t.display_name}
                      current={t.client_tenant_id === tenantId}
                      onSelect={() => chooseClient(t.client_tenant_id)}
                    />
                  ))}
                </>
              )}
              {q && clients.length === 0 && operations.length === 0 && (
                <div className="px-3 py-3 text-sm text-bluesphere-muted">
                  No Client matches “{query}”.
                </div>
              )}
            </>
          )}
          {!q && consented.length === 0 && (
            <div className="px-3 py-3 text-sm text-bluesphere-muted">
              {isLoading ? 'Loading Clients…' : 'No connected Clients yet.'}
            </div>
          )}
        </div>
      </MenuPanel>
    </div>
  );
}

function OpsHeading() {
  return (
    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-bluesphere-muted">
      BlueSphere operations
    </div>
  );
}

function Option({
  label,
  detail,
  indent = 0,
  current,
  muted = false,
  onSelect,
}: {
  label: string;
  /** Where a find result sits: "Simulint › Acme". */
  detail?: string;
  indent?: number;
  current: boolean;
  /** A suspended or offboarded organization: still selectable, shown dimmed. */
  muted?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={current}
      onClick={onSelect}
      className={clsx(menuItemClass, current && menuItemCurrentClass, muted && 'opacity-60')}
      style={indent > 0 ? { paddingLeft: `${0.75 + indent * 1}rem` } : undefined}
    >
      <span className="min-w-0 flex-1 truncate text-left">
        {label}
        {detail && <span className="block truncate text-xs text-bluesphere-muted">{detail}</span>}
      </span>
      {current && <CurrentDot />}
    </button>
  );
}
